import type { ReactNode } from 'react'

import { paths } from 'src/config/paths'
import SingleCoin from 'src/shared/assets/images/single-coin.png'
import { ChatFilled } from 'src/shared/components/icons/chat/chat-filled'
import { House } from 'src/shared/components/icons/feed/house'
import { Hourglass } from 'src/shared/components/icons/hourglass'
import { License } from 'src/shared/components/icons/license'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { BriefcaseFilled } from 'src/shared/components/icons/referrals/briefcase-filled'
import { Send } from 'src/shared/components/icons/referrals/send'
import { Star } from 'src/shared/components/icons/star'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { VisibilityLight } from 'src/shared/features/visibility/assets/visibility-light'
import { NotificationVerb } from 'src/shared/schemas/notifications'
import type { NotificationActivity } from 'src/shared/schemas/stream/notification'

import { NotificationSymbol } from '../components/notification-symbol'

type Notification = {
  getLink: (id?: string) => string
  getImage: (activity?: NotificationActivity) => ReactNode
  getSymbol: (hide?: boolean) => ReactNode
}

const getFirstActorId = (activity: NotificationActivity) => Number.parseInt(activity.actor.split(':')[1])

const FallbackImage = () => (
  <div className="flex size-[42px] items-center justify-center rounded-full bg-accent">
    <Iconmark size={28} />
  </div>
)

export const NotificationMap: Record<NotificationVerb, Notification> = {
  // REFERRAL SHARES
  [NotificationVerb.NEW_REFERRAL_SHARE]: {
    // received new referral share
    getLink: (id?: string) => (id ? paths.referrals.detail.referralShare.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => (
      <NotificationSymbol icon={BriefcaseFilled} iconColor="text-primary" bgColor="bg-secondaryInverse" />
    ),
  },
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_TIME_BASED]: {
    // receiver is expiring soon
    getLink: (id?: string) => (id ? paths.referrals.detail.referralShare.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={Hourglass} iconColor="text-primary" bgColor="bg-warning/50" />,
  },
  [NotificationVerb.REFERRAL_SHARE_SENDER_TIME_BASED]: {
    // sender is expiring soon
    getLink: (id?: string) => (id ? paths.referrals.detail.referral.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={Hourglass} iconColor="text-primary" bgColor="bg-warning/50" />,
  },
  [NotificationVerb.REFERRAL_SHARE_INTERESTED]: {
    // receiver is interested
    getLink: (id?: string) => (id ? paths.referrals.detail.referral.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={Send} iconColor="text-primary" bgColor="bg-secondaryInverse" />,
  },
  [NotificationVerb.REFERRAL_SHARE_APPROVED]: {
    // sender approved receiver
    getLink: (id?: string) => (id ? paths.referrals.detail.referralShare.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => (
      <NotificationSymbol icon={BriefcaseFilled} iconColor="text-primary" bgColor="bg-secondaryInverse" />
    ),
  },
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_DECLINED]: {
    // receiver declined sender
    getLink: (id?: string) => (id ? paths.referrals.detail.referral.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={Send} iconColor="text-white" bgColor="bg-destructive" />,
  },
  [NotificationVerb.REFERRAL_SHARE_SENDER_DENIED]: {
    // sender denied receiver
    getLink: (id?: string) => (id ? paths.referrals.detail.referralShare.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={BriefcaseFilled} iconColor="text-white" bgColor="bg-destructive" />,
  },
  [NotificationVerb.REFERRAL_SHARE_EXPIRED]: {
    // referral share expired
    getLink: (id?: string) => (id ? paths.referrals.detail.referralShare.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={BriefcaseFilled} iconColor="text-white" bgColor="bg-destructive" />,
  },

  // LICENSE VERIFICATION
  [NotificationVerb.LICENSE_VERIFICATION]: {
    getLink: () => paths.users.editLicenses.getHref(),
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-primary">
        <License size={24} className="text-white" />
      </div>
    ),
    getSymbol: () => null,
  },

  // POINTS
  [NotificationVerb.POINTS_RECEIVED]: {
    getLink: () => paths.users.points.getHref(),
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-points/25">
        <PointIcon size={24} />
      </div>
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.WEEKLY_RECAP]: {
    getLink: (date?: string) => (date ? paths.recap.getHref(date) : '#'),
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-primary">
        <VisibilityLight size={24} />
      </div>
    ),
    getSymbol: () => null,
  },

  // LEAD CREDIT REWARD
  [NotificationVerb.LEAD_CREDIT_REWARD_RECEIVED]: {
    getLink: () => paths.referrals.list.received.getHref(),
    getImage: () => (
      <img
        src={SingleCoin}
        alt="Lead Credit Reward"
        className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90"
      />
    ),
    getSymbol: () => null,
  },

  // FEED
  [NotificationVerb.NEW_COMMENT_ON_POST]: {
    getLink: (id?: string) => (id ? paths.feed.post.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={House} iconColor="text-white" bgColor="bg-[#8980EB]" />,
  },
  [NotificationVerb.NEW_COMMENT_ON_COMMENT]: {
    getLink: (id?: string) => (id ? paths.feed.comment.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={House} iconColor="text-white" bgColor="bg-[#8980EB]" />,
  },
  [NotificationVerb.NEW_LIKE_ON_POST]: {
    getLink: (id?: string) => (id ? paths.feed.post.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={House} iconColor="text-white" bgColor="bg-[#8980EB]" />,
  },
  [NotificationVerb.NEW_LIKE_ON_COMMENT]: {
    getLink: (id?: string) => (id ? paths.feed.comment.getHref(id) : '#'),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={House} iconColor="text-white" bgColor="bg-[#8980EB]" />,
  },

  // Partner Match
  [NotificationVerb.PARTNER_MATCH_WEEKLY_RESET]: {
    getLink: () => paths.home.getHref(),
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-partnerMatch">
        <Star size={20} className="text-white" />
      </div>
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.PARTNER_MATCH_UNLOCKED]: {
    getLink: (id?: string) => (id ? paths.chats.conversation.match.getHref(id) : paths.home.getHref()),
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-partnerMatch">
        <Star size={20} className="text-white" />
      </div>
    ),
    getSymbol: () => null,
  },

  // CHAT
  [NotificationVerb.NEW_MESSAGE]: {
    getLink: () => paths.chats.list.getHref(),
    getImage: (activity?: NotificationActivity) =>
      activity ? (
        <ProfileAvatar size={42} userId={getFirstActorId(activity)} />
      ) : (
        <div className="flex size-[42px] items-center justify-center rounded-full bg-secondaryInverse">
          <ChatFilled size={20} className="text-primary" />
        </div>
      ),
    getSymbol: (hide?: boolean) =>
      hide ? null : <NotificationSymbol icon={ChatFilled} iconColor="text-primary" bgColor="bg-secondaryInverse" />,
  },
  [NotificationVerb.REFERRAL_NEW_MESSAGE]: {
    getLink: (id?: string) =>
      id ? paths.chats.conversation.referral.getHref(`referral-share-${id}`) : paths.chats.list.getHref(),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={ChatFilled} iconColor="text-primary" bgColor="bg-secondaryInverse" />,
  },
  [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE]: {
    getLink: (id?: string) => (id ? paths.chats.conversation.match.getHref(id) : paths.home.getHref()),
    getImage: (activity?: NotificationActivity) =>
      activity ? (
        <ProfileAvatar size={42} userId={getFirstActorId(activity)} />
      ) : (
        <div className="flex size-[42px] items-center justify-center rounded-full bg-partnerMatch">
          <Star size={20} className="text-white" />
        </div>
      ),
    getSymbol: (hide?: boolean) =>
      hide ? null : <NotificationSymbol icon={ChatFilled} iconColor="text-white" bgColor="bg-partnerMatch" />,
  },
  [NotificationVerb.NETWORK_NEW_MESSAGE]: {
    getLink: (id?: string) => (id ? paths.chats.conversation.network.getHref(id) : paths.home.getHref()),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={ChatFilled} iconColor="text-white" bgColor="bg-primary" />,
  },
  [NotificationVerb.SNEAK_NEW_MESSAGE]: {
    getLink: (id?: string) => (id ? paths.chats.conversation.sneak.getHref(id) : paths.home.getHref()),
    getImage: (activity?: NotificationActivity) =>
      activity ? <ProfileAvatar size={42} userId={getFirstActorId(activity)} /> : <FallbackImage />,
    getSymbol: () => <NotificationSymbol icon={ChatFilled} iconColor="text-white" bgColor="bg-sneak" />,
  },

  // LEAD INQUIRY
  [NotificationVerb.LEAD_INQUIRY_RECEIVED]: {
    getLink: () => paths.referrals.list.received.getHref(),
    getImage: () => (
      <img src={SingleCoin} alt="Lead Inquiry" className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90" />
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.LEAD_INQUIRY_REMINDER]: {
    getLink: () => paths.referrals.list.received.getHref(),
    getImage: () => (
      <img src={SingleCoin} alt="Lead Inquiry" className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90" />
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.LEAD_INQUIRY_COMPETITION]: {
    getLink: () => '#',
    getImage: () => (
      <img src={SingleCoin} alt="Lead Inquiry" className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90" />
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.LEAD_INQUIRY_TAKEN]: {
    getLink: () => '#',
    getImage: () => (
      <img src={SingleCoin} alt="Lead Inquiry" className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90" />
    ),
    getSymbol: () => null,
  },
  [NotificationVerb.ONE_TIME]: {
    getLink: () => '#',
    getImage: () => (
      <img src={SingleCoin} alt="one time" className="size-10 max-h-10 min-h-10 min-w-10 max-w-10 -rotate-90" />
    ),
    getSymbol: () => null,
  },

  // SYSTEM
  [NotificationVerb.SYSTEM]: {
    getLink: () => '#',
    getImage: () => (
      <div className="flex size-[42px] items-center justify-center rounded-full bg-secondary/35">
        <Iconmark size={28} />
      </div>
    ),
    getSymbol: () => null,
  },
}
